// Pure, deterministic comparison for immutable Stage 5 quote snapshots.
// No clocks, storage, browser APIs, or network state are consulted.

export const QUOTE_SNAPSHOT_DIFF_SCHEMA_VERSION = 1;

const MISSING = Symbol('missing');

export interface ScopePairCandidate {
    originalIndex: number;
    finalIndex: number;
    rank: number;
    similarity: number;
    reason: 'id' | 'code' | 'title' | 'content';
}

export interface MatchedScopeItem extends ScopePairCandidate {
    original: any;
    final: any;
}

export interface MatchQuoteScopeItemsResult {
    matched: MatchedScopeItem[];
    added: Array<{ item: any; index: number }>;
    removed: Array<{ item: any; index: number }>;
}

export interface QuoteSnapshotDiffChange {
    kind: 'add' | 'remove' | 'replace' | 'move';
    path: string;
    before?: any;
    after?: any;
}

export interface QuoteSnapshotDiffReport {
    schemaVersion: number;
    originalFingerprint: string;
    finalFingerprint: string;
    hasChanges: boolean;
    summary: {
        changeCount: number;
        addedFields: number;
        removedFields: number;
        replacedFields: number;
        movedItems: number;
        scope: {
            originalItems: number;
            finalItems: number;
            matchedItems: number;
            addedItems: number;
            removedItems: number;
            changedItems: number;
        };
    };
    scope: {
        matched: Array<{
            identity: string;
            matchReason: string;
            similarity: number;
            originalIndex: number;
            finalIndex: number;
            changedPaths: string[];
        }>;
        added: Array<{ identity: string; finalIndex: number }>;
        removed: Array<{ identity: string; originalIndex: number }>;
    };
    changes: QuoteSnapshotDiffChange[];
}

function isRecord(value: unknown): value is Record<string, any> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function normaliseText(value: unknown): string {
    return String(value ?? '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}

function tokenSet(value: unknown): Set<string> {
    return new Set(normaliseText(value).split(/\s+/).filter(Boolean));
}

function diceSimilarity(a: unknown, b: unknown): number {
    const left = tokenSet(a);
    const right = tokenSet(b);
    if (left.size === 0 && right.size === 0) return 1;
    if (left.size === 0 || right.size === 0) return 0;
    let overlap = 0;
    for (const token of left) {
        if (right.has(token)) overlap += 1;
    }
    return (2 * overlap) / (left.size + right.size);
}

function canonicalise(value: unknown, seen: WeakSet<object> = new WeakSet()): any {
    if (value === undefined) return { $type: 'undefined' };
    if (typeof value === 'number' && !Number.isFinite(value)) {
        return { $type: 'number', value: String(value) };
    }
    if (value === null || typeof value !== 'object') return value;
    if (value instanceof Date) return { $type: 'date', value: value.toISOString() };
    if (seen.has(value)) throw new TypeError('Quote snapshots must not contain circular references');
    seen.add(value);
    const result: any = Array.isArray(value)
        ? value.map((item: any): any => canonicalise(item, seen))
        : Object.keys(value as Record<string, any>).sort().reduce((out: Record<string, any>, key: string) => {
            out[key] = canonicalise((value as Record<string, any>)[key], seen);
            return out;
        }, {});
    seen.delete(value);
    return result;
}

/** Stable JSON form used by evidence artifacts and deterministic fingerprints. */
export function stableStringify(value: unknown): string {
    return JSON.stringify(canonicalise(value));
}

/**
 * Small deterministic content fingerprint. This is an identity/checksum, not a
 * cryptographic signature; callers that need tamper proofing should SHA-256 the
 * stableStringify output at the artifact boundary.
 */
export function fingerprintValue(value: unknown): string {
    const text = stableStringify(value);
    let hash = 0x811c9dc5;
    for (let index = 0; index < text.length; index += 1) {
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193);
    }
    return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

/** Accept either a raw quote or the immutable snapshot envelope that contains it. */
export function unwrapQuoteSnapshot(value: unknown): any {
    if (!isRecord(value)) return value;
    if (isRecord(value.payload)
        && (value.kind === 'quote' || Number(value.stage) === 5 || value.metadata?.immutable === true)) {
        return value.payload;
    }
    return value;
}

function reportValue(value: unknown): any {
    if (value === MISSING || value === undefined) return null;
    return canonicalise(value);
}

function pointerSegment(value: unknown): string {
    return String(value).replace(/~/g, '~0').replace(/\//g, '~1');
}

function pointer(parent: string, child: unknown): string {
    return `${parent}/${pointerSegment(child)}`;
}

function itemIdentity(item: any, index: number): string {
    const id = String(item?.id ?? '').trim();
    if (id) return `id:${id}`;
    const code = String(item?.code ?? '').trim();
    if (code) return `code:${code}`;
    const title = normaliseText(item?.title || item?.name);
    if (title) return `title:${title}`;
    return `index:${index}`;
}

function scopeText(item: any): string {
    const inclusions = Array.isArray(item?.inclusions)
        ? item.inclusions.map((entry: any) => typeof entry === 'string' ? entry : entry?.text || entry?.label || '')
        : [item?.inclusions || ''];
    return [
        item?.title,
        item?.name,
        item?.description,
        item?.descriptionBrief,
        item?.description_brief,
        ...inclusions,
    ].filter(Boolean).join(' ');
}

function scopePairCandidate(original: any, final: any, originalIndex: number, finalIndex: number): ScopePairCandidate | null {
    const originalId = String(original?.id ?? '').trim();
    const finalId = String(final?.id ?? '').trim();
    if (originalId && finalId && originalId === finalId) {
        return { originalIndex, finalIndex, rank: 4, similarity: 1, reason: 'id' };
    }

    const originalCode = String(original?.code ?? '').trim();
    const finalCode = String(final?.code ?? '').trim();
    if (originalCode && finalCode && originalCode === finalCode) {
        const similarity = diceSimilarity(scopeText(original), scopeText(final));
        // Sequential line codes such as "1" and "2" are presentation
        // labels, not durable identity. Only elevate an equal code when the
        // underlying work remains recognisably the same; otherwise allow the
        // title/content matcher to pair the rows (or leave an invented row
        // unmatched).
        if (similarity >= 0.45) {
            return {
                originalIndex,
                finalIndex,
                rank: 3,
                similarity,
                reason: 'code',
            };
        }
    }

    const originalTitle = normaliseText(original?.title || original?.name);
    const finalTitle = normaliseText(final?.title || final?.name);
    if (originalTitle && originalTitle === finalTitle) {
        return { originalIndex, finalIndex, rank: 2, similarity: 1, reason: 'title' };
    }

    const similarity = diceSimilarity(scopeText(original), scopeText(final));
    if (similarity < 0.35) return null;
    return { originalIndex, finalIndex, rank: 1, similarity, reason: 'content' };
}

/**
 * Deterministically match scope rows by immutable ID, code, title, then content.
 * Greedy selection is stable because every tie ends at original/final indexes.
 */
export function matchQuoteScopeItems(originalScope: any[] = [], finalScope: any[] = []): MatchQuoteScopeItemsResult {
    const original = Array.isArray(originalScope) ? originalScope : [];
    const final = Array.isArray(finalScope) ? finalScope : [];
    const candidates: ScopePairCandidate[] = [];

    for (let originalIndex = 0; originalIndex < original.length; originalIndex += 1) {
        for (let finalIndex = 0; finalIndex < final.length; finalIndex += 1) {
            const candidate = scopePairCandidate(
                original[originalIndex],
                final[finalIndex],
                originalIndex,
                finalIndex,
            );
            if (candidate) candidates.push(candidate);
        }
    }

    candidates.sort((a, b) => (
        b.rank - a.rank
        || b.similarity - a.similarity
        || a.originalIndex - b.originalIndex
        || a.finalIndex - b.finalIndex
    ));

    const usedOriginal = new Set<number>();
    const usedFinal = new Set<number>();
    const matched: MatchedScopeItem[] = [];
    for (const candidate of candidates) {
        if (usedOriginal.has(candidate.originalIndex) || usedFinal.has(candidate.finalIndex)) continue;
        usedOriginal.add(candidate.originalIndex);
        usedFinal.add(candidate.finalIndex);
        matched.push({
            ...candidate,
            similarity: Number(candidate.similarity.toFixed(4)),
            original: original[candidate.originalIndex],
            final: final[candidate.finalIndex],
        });
    }

    matched.sort((a, b) => a.finalIndex - b.finalIndex || a.originalIndex - b.originalIndex);
    return {
        matched,
        added: final
            .map((item, index) => ({ item, index }))
            .filter(({ index }) => !usedFinal.has(index)),
        removed: original
            .map((item, index) => ({ item, index }))
            .filter(({ index }) => !usedOriginal.has(index)),
    };
}

function valuesEqual(before: unknown, after: unknown): boolean {
    if (before === after) return true;
    return stableStringify(before) === stableStringify(after);
}

function appendChange(changes: QuoteSnapshotDiffChange[], kind: 'add' | 'remove' | 'replace' | 'move', path: string, before: unknown = MISSING, after: unknown = MISSING): void {
    changes.push({
        kind,
        path: path || '/',
        ...(before !== MISSING ? { before: reportValue(before) } : {}),
        ...(after !== MISSING ? { after: reportValue(after) } : {}),
    });
}

function walkDiff(before: unknown, after: unknown, path: string, changes: QuoteSnapshotDiffChange[]): void {
    if (before === MISSING) {
        appendChange(changes, 'add', path, MISSING, after);
        return;
    }
    if (after === MISSING) {
        appendChange(changes, 'remove', path, before, MISSING);
        return;
    }
    if (valuesEqual(before, after)) return;

    if (Array.isArray(before) && Array.isArray(after)) {
        const length = Math.max(before.length, after.length);
        for (let index = 0; index < length; index += 1) {
            walkDiff(
                index < before.length ? before[index] : MISSING,
                index < after.length ? after[index] : MISSING,
                pointer(path, index),
                changes,
            );
        }
        return;
    }

    if (isRecord(before) && isRecord(after)) {
        const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort();
        for (const key of keys) {
            walkDiff(
                Object.prototype.hasOwnProperty.call(before, key) ? before[key] : MISSING,
                Object.prototype.hasOwnProperty.call(after, key) ? after[key] : MISSING,
                pointer(path, key),
                changes,
            );
        }
        return;
    }

    appendChange(changes, 'replace', path, before, after);
}

function withoutScope(quote: unknown): any {
    if (!isRecord(quote)) return quote;
    const { scope: _scope, ...rest } = quote;
    return rest;
}

function uniqueScopeIdentity(base: string, seen: Map<string, number>): string {
    const count = seen.get(base) || 0;
    seen.set(base, count + 1);
    return count === 0 ? base : `${base}#${count + 1}`;
}

/**
 * Produce a field-level original-vs-final report. Scope rows retain identity
 * across reordering and copy edits, so paired customer-zero evidence does not
 * collapse into noisy array-index replacements.
 */
export function diffQuoteSnapshots(originalSnapshot: unknown, finalSnapshot: unknown): QuoteSnapshotDiffReport {
    const original = unwrapQuoteSnapshot(originalSnapshot);
    const final = unwrapQuoteSnapshot(finalSnapshot);
    const changes: QuoteSnapshotDiffChange[] = [];
    walkDiff(withoutScope(original), withoutScope(final), '', changes);

    const originalScope = Array.isArray(original?.scope) ? original.scope : [];
    const finalScope = Array.isArray(final?.scope) ? final.scope : [];
    const scopeMatches = matchQuoteScopeItems(originalScope, finalScope);
    const seenIdentities = new Map<string, number>();
    const matched: QuoteSnapshotDiffReport['scope']['matched'] = [];

    for (const match of scopeMatches.matched) {
        const baseIdentity = itemIdentity(match.final, match.finalIndex)
            || itemIdentity(match.original, match.originalIndex);
        const identity = uniqueScopeIdentity(baseIdentity, seenIdentities);
        const itemChanges: QuoteSnapshotDiffChange[] = [];
        walkDiff(match.original, match.final, pointer('/scope', identity), itemChanges);
        changes.push(...itemChanges);
        if (match.originalIndex !== match.finalIndex) {
            changes.push({
                kind: 'move',
                path: pointer('/scope', identity),
                before: match.originalIndex,
                after: match.finalIndex,
            });
        }
        matched.push({
            identity,
            matchReason: match.reason,
            similarity: match.similarity,
            originalIndex: match.originalIndex,
            finalIndex: match.finalIndex,
            changedPaths: itemChanges.map(change => change.path).sort(),
        });
    }

    const added = scopeMatches.added.map(({ item, index }) => {
        const identity = uniqueScopeIdentity(itemIdentity(item, index), seenIdentities);
        appendChange(changes, 'add', pointer('/scope', identity), MISSING, item);
        return { identity, finalIndex: index };
    });
    const removed = scopeMatches.removed.map(({ item, index }) => {
        const identity = uniqueScopeIdentity(itemIdentity(item, index), seenIdentities);
        appendChange(changes, 'remove', pointer('/scope', identity), item, MISSING);
        return { identity, originalIndex: index };
    });

    const kindOrder: Record<string, number> = { remove: 0, add: 1, replace: 2, move: 3 };
    changes.sort((a, b) => (
        a.path.localeCompare(b.path)
        || (kindOrder[a.kind] ?? 99) - (kindOrder[b.kind] ?? 99)
    ));

    const countKind = (kind: string) => changes.filter(change => change.kind === kind).length;
    return {
        schemaVersion: QUOTE_SNAPSHOT_DIFF_SCHEMA_VERSION,
        originalFingerprint: fingerprintValue(original),
        finalFingerprint: fingerprintValue(final),
        hasChanges: changes.length > 0,
        summary: {
            changeCount: changes.length,
            addedFields: countKind('add'),
            removedFields: countKind('remove'),
            replacedFields: countKind('replace'),
            movedItems: countKind('move'),
            scope: {
                originalItems: originalScope.length,
                finalItems: finalScope.length,
                matchedItems: matched.length,
                addedItems: added.length,
                removedItems: removed.length,
                changedItems: matched.filter(item => item.changedPaths.length > 0).length,
            },
        },
        scope: { matched, added, removed },
        changes,
    };
}
